import { addDays, type IsoDate, monthEndOf } from "@/lib/time/dates";

/**
 * My Expenses rules (spec docs/specs/2026-10-30-expenses-design.md, R1-R14). Pure and shared by
 * the server and the browser; the gateway repeats each rule in its own WHERE.
 */

/** R2: the types a new claim may use. Department POs stays on old claims only. */
export const EMPLOYEE_TYPE_ENUMS: readonly string[] = [
  "officeequipment",
  "gym",
  "insurance",
  "other",
];

/** R2 as reversed by Zack 2026-10-30: Department POs (and Demand POs among them) take a PO. */
const PO_TYPE_ENUM = "departmentpo";
export const isPoType = (typeEnum: string | null): boolean => typeEnum === PO_TYPE_ENUM;
export const NEW_CLAIM_TYPE_ENUMS: readonly string[] = [...EMPLOYEE_TYPE_ENUMS, PO_TYPE_ENUM];

export const APPROVAL_STATUSES = ["draft", "pending", "approved", "rejected", "escalated"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const PAYMENT_STATUSES = ["notapproved", "pendingpayment", "paid", "late"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  escalated: "Escalated",
};

export const PAYMENT_LABELS: Record<PaymentStatus, string> = {
  notapproved: "Not approved",
  pendingpayment: "Pending payment",
  paid: "Paid",
  late: "Late",
};

/** The term when the year names none (R9). */
const DEFAULT_PAYMENT_DAYS = 60;

export const EXPENSE_MESSAGES = {
  notFound: "That expense isn't available any more.",
  locked: "This expense can't be changed.",
  closedMonth: "The reimbursement period selected is now closed. Please submit for another period.",
  noMonth: "Pick an open month.",
  badType: "Pick a type.",
  noLines: "Add at least one line.",
  notEligibleOffice:
    "You are not currently eligible for the Annual Office Equipment stipend. If this is an error, please notify your manager.",
  notEligibleWellness:
    "You are not currently eligible for the Wellness stipend. If this is an error, please notify your manager.",
  overYear: "Amount submitted exceeds annual limit",
  overMonth: "Amount submitted exceeds monthly limit",
  noLimit: "No stipend limit is set for that year. Ask Finance.",
  noVinylUser: "Your sign-in has no Vinyl login, so this can't be saved. Ask Finance.",
  notApprover: "You can't decide this expense.",
  noOpenMonth: "No month is open for expenses.",
  noCopy: "This expense can't be copied.",
  noPo: "Pick a purchase order.",
  poYear: "The reimbursement submitted does not fall under the fiscal year of the selected PO.",
  copyPartial: "The copy didn't finish. Nothing was added.",
  copyEmpty: "The copy has no lines yet. Delete that draft and try again.",
} as const;

export const isEditable = (status: ApprovalStatus | null): boolean =>
  status === "draft" || status === "rejected";

const isStipend = (typeEnum: string): boolean =>
  typeEnum === "officeequipment" || typeEnum === "gym";

/** Copy needs a type a new claim may use (R2, R13). */
export const canCopy = (typeEnum: string): boolean => NEW_CLAIM_TYPE_ENUMS.includes(typeEnum);

export const cents = (amount: number): number => Math.round(amount * 100);

export const isWholeCents = (amount: number): boolean =>
  Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-6;

/** R7: null when the person may claim this type, else the message to show. */
export function eligibilityRefusal(
  typeEnum: string,
  eligible: { office: boolean; wellness: boolean },
): string | null {
  if (typeEnum === "officeequipment" && !eligible.office) return EXPENSE_MESSAGES.notEligibleOffice;
  if (typeEnum === "gym" && !eligible.wellness) return EXPENSE_MESSAGES.notEligibleWellness;
  return null;
}

/** The part of a claim the limit math reads. */
export interface SpendClaim {
  id: string;
  typeId: string;
  periodId: string;
  approval: ApprovalStatus | null;
  total: number;
}

/** DECISIONS 2026-10-15: stipend balance and limit check both count approved + pending. */
export function committedSpend(
  claims: readonly SpendClaim[],
  typeId: string,
  scopePeriodIds: readonly string[],
  excludeId: string | null,
): number {
  const scope = new Set(scopePeriodIds);
  const total = claims
    .filter(
      (c) =>
        c.typeId === typeId &&
        scope.has(c.periodId) &&
        c.id !== excludeId &&
        (c.approval === "approved" || c.approval === "pending"),
    )
    .reduce((sum, c) => sum + cents(c.total), 0);
  return total / 100;
}

/** R8: null when spend + this claim fits the limit, else the message to show. */
export function limitRefusal(
  typeEnum: string,
  limit: number | null,
  committed: number,
  amount: number,
): string | null {
  if (!isStipend(typeEnum)) return null;
  if (limit === null) return EXPENSE_MESSAGES.noLimit;
  if (cents(committed) + cents(amount) <= cents(limit)) return null;
  return typeEnum === "gym" ? EXPENSE_MESSAGES.overMonth : EXPENSE_MESSAGES.overYear;
}

/** R9: the end of the month of (submit date + the term). */
export function estimatedPaidDate(submitted: IsoDate, termDays: number | null): IsoDate {
  return monthEndOf(addDays(submitted, termDays ?? DEFAULT_PAYMENT_DAYS));
}
